import type {
  ModuleChoice,
  ModuleScene,
  TranscriptEntry,
} from "../gamebook/contracts/types";

type Props = {
  scene: ModuleScene;
  actions: ModuleChoice[];
  transcript: TranscriptEntry[];
  command: string;
  error?: string;
  ending: string | null;
  expanded: boolean;
  onToggleExpanded: () => void;
  onCommandChange: (value: string) => void;
  onSubmitCommand: () => void;
  onAction: (actionId: string) => void;
};

/**
 * Literary reading surface for the focused CHIP.
 * Compact: title + primary actions first (safe above nav), then lead line.
 * Expanded: full body, local objects, transcript, command parser.
 */
export function GamebookSheet({
  scene,
  actions,
  transcript,
  command,
  error,
  ending,
  expanded,
  onToggleExpanded,
  onCommandChange,
  onSubmitCommand,
  onAction,
}: Props) {
  const recent = transcript.slice(-6);
  const lead = scene.body[0] ?? "";
  // Keep the compact surface playable for scenes with one or two follow-up
  // choices; the sheet itself scrolls when a scene genuinely has more.
  const compactActionLimit = 6;
  const primaryActions = actions.slice(
    0,
    expanded ? actions.length : compactActionLimit,
  );

  const actionBlock =
    !ending && primaryActions.length > 0 ? (
      <div class="gamebook-actions">
        {primaryActions.map((action) => (
          <button
            type="button"
            class="invoke-button gamebook-action"
            key={action.id}
            title={action.detail}
            onClick={() => onAction(action.id)}
          >
            {action.label}
          </button>
        ))}
        {!expanded && actions.length > compactActionLimit && (
          <p class="meta">
            +{actions.length - compactActionLimit} more in expanded focus
          </p>
        )}
      </div>
    ) : null;

  return (
    <aside
      class={`bottom-sheet gamebook-sheet ${expanded ? "expanded" : "compact"}`}
      aria-label={`${scene.title} passage`}
    >
      <span class="sheet-grab" aria-hidden="true" />
      <div class="chip-sheet-head">
        <div class="chip-id">{scene.id.toUpperCase()}</div>
        <div class="chip-id">{scene.type}</div>
      </div>
      <h2>{scene.title}</h2>
      {scene.subtitle && <p class="meta gamebook-sub">{scene.subtitle}</p>}

      {/* Compact: actions first so they clear the nav bar on tall phones */}
      {!expanded && actionBlock}

      <div class="gamebook-body">
        {expanded
          ? scene.body.map((line) => <p key={line}>{line}</p>)
          : lead && <p class="gamebook-lead">{lead}</p>}
      </div>

      {expanded && actionBlock}

      {expanded && (scene.objects?.length || scene.present?.length) && (
        <div class="gamebook-local">
          {scene.objects?.length ? (
            <div>
              <div class="meta">OBJECTS</div>
              <p>{scene.objects.join(" · ")}</p>
            </div>
          ) : null}
          {scene.present?.length ? (
            <div>
              <div class="meta">PRESENT</div>
              <p>{scene.present.join(" · ")}</p>
            </div>
          ) : null}
        </div>
      )}

      {ending && (
        <div class="gamebook-ending" role="status">
          ENDING RESOLVED // {ending}
        </div>
      )}

      {expanded && (
        <div class="gamebook-transcript" aria-live="polite">
          {recent.map((entry, i) => (
            <div
              class={`tx tx-${entry.kind.toLowerCase()}`}
              key={`${i}-${entry.text}`}
            >
              {entry.text}
            </div>
          ))}
        </div>
      )}

      {expanded && !ending && (
        <form
          class="gamebook-command"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmitCommand();
          }}
        >
          <label class="meta" for="mud-command">
            LOCAL COMMAND
          </label>
          <div class="gamebook-command-row">
            <input
              id="mud-command"
              value={command}
              placeholder="inspect the hatch"
              autocomplete="off"
              onInput={(e) =>
                onCommandChange((e.target as HTMLInputElement).value)
              }
            />
            <button type="submit" class="deck-chamfer-button">
              RUN
            </button>
          </div>
          {error && (
            <div class="failure-banner gamebook-error" role="alert">
              {error}
            </div>
          )}
        </form>
      )}

      <div class="gamebook-focus-row">
        <button
          type="button"
          class="deck-chamfer-button gamebook-focus-toggle"
          aria-pressed={expanded}
          onClick={onToggleExpanded}
        >
          {expanded ? "COMPACT FOCUS" : "EXPAND PASSAGE"}
        </button>
        <p class="meta gamebook-fixture-tag">
          DEVELOPMENT FIXTURE / NON-AUTHORITATIVE
        </p>
      </div>
    </aside>
  );
}
